import React, { useState } from 'react';
import { useTranslation } from '../../../../shared/i18n';
import { dailyTargetsApi } from '../../api/guardians';
import ModeSelector from '../../../goal-setting/components/ModeSelector';
import BasicModeInput from '../../../goal-setting/components/BasicModeInput';
import AdvancedModeInput from '../../../goal-setting/components/AdvancedModeInput';

// Map simple keys used by child components to i18n paths
const I18N_KEY_MAP = {
  // Mode selector
  basicMode: 'children.goal_setting.mode_selector.basic_mode',
  advancedMode: 'children.goal_setting.mode_selector.advanced_mode',

  // Input labels
  xpTarget: 'children.goal_setting.input_labels.xp_target',
  selectDailyTarget: 'children.goal_setting.input_labels.select_daily_target',
  day: 'children.goal_setting.input_labels.day',
  xpGoal: 'children.goal_setting.input_labels.xp_goal',

  // XP option descriptions
  xp100Description: 'children.goal_setting.xp_descriptions.xp100',
  xp200Description: 'children.goal_setting.xp_descriptions.xp200',
  xp500Description: 'children.goal_setting.xp_descriptions.xp500',
  xp750Description: 'children.goal_setting.xp_descriptions.xp750',
  xp1000Description: 'children.goal_setting.xp_descriptions.xp1000',

  // Days of week
  monday: 'children.goal_setting.days.monday',
  tuesday: 'children.goal_setting.days.tuesday',
  wednesday: 'children.goal_setting.days.wednesday',
  thursday: 'children.goal_setting.days.thursday',
  friday: 'children.goal_setting.days.friday',
  saturday: 'children.goal_setting.days.saturday',
  sunday: 'children.goal_setting.days.sunday',

  // Upgrade modal
  upgradeModalTitle: 'children.goal_setting.upgrade_modal.title',
  upgradeModalDescription: 'children.goal_setting.upgrade_modal.description',
  upgradeModalClose: 'children.goal_setting.upgrade_modal.close',
  upgradeModalUpgrade: 'children.goal_setting.upgrade_modal.upgrade'
};

const DEFAULT_TARGET_XP = 100;
const DEFAULT_FREE_MAX_XP = 200;

export const WizardGoalStep = ({
  childId,
  childName,
  initialValue = DEFAULT_TARGET_XP,
  hasSubscription = false,
  maxFreeXp = DEFAULT_FREE_MAX_XP,
  onContinue,
  onSkip,
  onBack,
  onUpgrade
}) => {
  const { t: i18nT } = useTranslation();
  // Free kids can only use basic mode
  const [mode, setMode] = useState('basic');
  const [targetXp, setTargetXp] = useState(initialValue);
  const maxXp = hasSubscription ? 1000 : maxFreeXp;
  const [weekdayXp, setWeekdayXp] = useState({
    mondayXp: initialValue,
    tuesdayXp: initialValue,
    wednesdayXp: initialValue,
    thursdayXp: initialValue,
    fridayXp: initialValue,
    saturdayXp: initialValue,
    sundayXp: initialValue
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Translation function that maps simple keys to i18n paths
  const t = (key) => {
    const i18nKey = I18N_KEY_MAP[key];
    return i18nKey ? i18nT(i18nKey) : key;
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build the target data based on mode
      const targetData = {
        mode: mode,
        target_xp: targetXp
      };

      // Add weekday XP if in advanced mode
      if (mode === 'advanced') {
        targetData.monday_xp = weekdayXp.mondayXp;
        targetData.tuesday_xp = weekdayXp.tuesdayXp;
        targetData.wednesday_xp = weekdayXp.wednesdayXp;
        targetData.thursday_xp = weekdayXp.thursdayXp;
        targetData.friday_xp = weekdayXp.fridayXp;
        targetData.saturday_xp = weekdayXp.saturdayXp;
        targetData.sunday_xp = weekdayXp.sundayXp;
      }

      await dailyTargetsApi.create(childId, targetData);

      onContinue(targetXp);
    } catch (err) {
      const errorData = err.response?.data?.error;
      const errorMessage = typeof errorData === 'string' ? errorData : errorData?.message || 'Failed to save daily target';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h4 className="mb-3" data-testid="wizard-step-title">Đặt Mục Tiêu</h4>
      <p className="text-muted mb-4">
        Đặt mục tiêu học tập hàng ngày cho {childName}
      </p>

      {error && (
        <div className="alert alert-danger mb-3">
          {error}
        </div>
      )}

      {/* Show mode selector for all users */}
      <ModeSelector
        mode={mode}
        onModeChange={setMode}
        t={t}
      />

      {mode === 'basic' && (
        <BasicModeInput
          targetXp={targetXp}
          onChange={setTargetXp}
          t={t}
          maxXp={maxXp}
          onUpgrade={onUpgrade}
        />
      )}

      {mode === 'advanced' && (
        <AdvancedModeInput
          weekdayXp={weekdayXp}
          onChange={setWeekdayXp}
          t={t}
          maxXp={maxXp}
          childId={childId}
          dashboardPath="/guardian"
          onUpgrade={onUpgrade}
        />
      )}

      {/* Action buttons */}
      <div className="d-flex justify-content-between">
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onBack}
          >
            Quay Lại
          </button>
          <a
            href="#"
            className="btn btn-link text-muted"
            onClick={(e) => { e.preventDefault(); onSkip(); }}
          >
            Bỏ Qua
          </a>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Đang lưu...
            </>
          ) : (
            'Tiếp Tục'
          )}
        </button>
      </div>
    </div>
  );
};
